import React, { useState, Children, lazy, Component } from 'react';
import { Link } from 'react-router-dom';
import { BookIcon, MapPinIcon, FlameIcon, BookOpenIcon, HeartIcon, UserIcon, SettingsIcon, TagIcon, CalendarIcon, EditIcon, InstagramIcon, TwitterIcon, GlobeIcon, ChevronDownIcon, ChevronUpIcon, GiftIcon } from 'lucide-react';
import BackButton from '../components/UI/BackButton';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
const ShopIcon = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-13090" data-mp="3ju383lsknzog">
    <path d="M3 9l1.5 8h15L21 9M3 9h18M3 9l2-5h14l2 5M9 14v1m6-1v1" data-id="element-13091" data-mp="356ryvqjv4rri" />
  </svg>;
// Mock donation data for readers
const readerDonationData = {
  isPublic: true,
  totalDonated: 156.0,
  causes: [{
    id: '1',
    name: "Children's Literacy Foundation",
    slug: 'childrens-literacy-foundation'
  }, {
    id: '2',
    name: 'Rural Library Project',
    slug: 'rural-library-project'
  }]
};
export default function ReaderProfile() {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Sample profile data
  const [profile] = useState({
    username: 'bookworm42',
    displayName: 'Sarah Johnson',
    pronouns: 'she/her',
    location: 'Portland, OR',
    joinDate: 'January 2022',
    bio: "Avid reader with a passion for fantasy and romance. Always looking for stories with strong character development and immersive worlds. When I'm not reading, I'm probably thinking about reading.",
    genres: ['Fantasy', 'Romance', 'Mystery', 'Historical Fiction'],
    tropes: ['Enemies to Lovers', 'Found Family', 'Morally Grey Characters', 'Slow Burn'],
    burnLevel: 'Medium to Slow',
    format: 'eBook, Paperback',
    comfortLevel: 'Medium Spice',
    triggers: 'Animal cruelty, graphic violence',
    currentRead: 'The Invisible Life of Addie LaRue by V.E. Schwab',
    recentlyRead: [{
      title: 'A Court of Silver Flames',
      author: 'Sarah J. Maas',
      rating: 4.5,
      series: 'ACOTAR #4'
    }, {
      title: 'The Love Hypothesis',
      author: 'Ali Hazelwood',
      rating: 5
    }, {
      title: 'Legendborn',
      author: 'Tracy Deonn',
      rating: 4,
      series: 'Legendborn #1'
    }],
    tbr: ['Fourth Wing', 'The Atlas Six', 'Iron Flame'],
    following: {
      authors: ['Sarah J. Maas', 'V.E. Schwab', 'Ali Hazelwood'],
      vendors: ['Bookish Box', 'Illumicrate'],
      readers: ['readingwithwine', 'bookishluna', 'fantasyreader']
    },
    socialLinks: [{
      platform: 'Instagram',
      url: 'https://instagram.com/bookworm42'
    }, {
      platform: 'Twitter',
      url: 'https://twitter.com/bookworm42'
    }, {
      platform: 'Goodreads',
      url: 'https://goodreads.com/bookworm42'
    }],
    vibeImages: [{
      url: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80'
    }, {
      url: 'https://images.unsplash.com/photo-1598646506899-ac6be1060f4b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80'
    }, {
      url: 'https://images.unsplash.com/photo-1515876305430-f06edab8282a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80'
    }]
  });
  const [activeTab, setActiveTab] = useState('about');
  const [bookshelfView, setBookshelfView] = useState('recentlyRead');
  const [showSectionContent, setShowSectionContent] = useState(true);
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-13092" data-mp="14c9u93h3o9qi">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-13093" data-mp="3k0jo1ofmlp0t">
        <div className="mb-6" data-id="element-13094" data-mp="3p0fqvkcl7aes">
          <BackButton data-id="element-13095" data-mp="3p1qpz6v37tzk" />
        </div>

        {/* Profile Header */}
        <div className={`rounded-lg shadow-md overflow-hidden mb-6 ${dk.card}`} data-id="element-13096" data-mp="92697pl2mdz9">
          {/* Cover/Banner Image */}
          <div className="h-48 sm:h-56 bg-gradient-to-r from-indigo-400 via-pink-500 to-red-500 relative" data-id="element-13097" data-mp="4bfz5z1p9my8">
            <div className="absolute bottom-0 left-0 w-full h-full bg-black bg-opacity-20" data-id="element-13098" data-mp="3vj2beg2hixqp"></div>
          </div>

          {/* Profile Info */}
          <div className="relative px-6 pb-6" data-id="element-13099" data-mp="3qh36v2v0dus6">
            {/* Profile Picture */}
            <div className={`absolute -top-16 left-6 h-32 w-32 sm:h-36 sm:w-36 rounded-full border-4 overflow-hidden shadow-md flex items-center justify-center ${dk.border} ${dk.isDark ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-500'}`} data-id="element-13100" data-mp="s0iqxzq1sw6g">
              <UserIcon className="h-16 w-16 sm:h-20 sm:w-20" data-id="element-13101" data-mp="d3hytv3y2z7g" />
            </div>

            {/* Edit Profile Button */}
            <div className="flex justify-end pt-4 space-x-2" data-id="element-13102" data-mp="32r8wqt22qkbm">
              <Link to="/account-settings" className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${rt.outlineButton}`} data-id="element-13103" data-mp="2xt7gsgzy314d">
                <EditIcon className="h-4 w-4 mr-2" data-id="element-13104" data-mp="1ic2mgb4zoe6x" />
                Edit Profile
              </Link>
            </div>

            {/* User Info */}
            <div className="mt-6" data-id="element-13105" data-mp="1s0uc2cmkumeo">
              <h1 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-13106" data-mp="1s25p731wx6ig">
                {profile.displayName}
              </h1>
              <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 mt-1 text-sm ${dk.textSecondary}`} data-id="element-13107" data-mp="1wzxju4txz6qc">
                <span data-id="element-13108" data-mp="26156tqr0cl54">@{profile.username}</span>
                {profile.pronouns && <span data-id="element-13109" data-mp="25yssdnkgkswt">{profile.pronouns}</span>}
                {profile.location && <span className="flex items-center" data-id="element-13110" data-mp="2az6w4tf11gp1">
                    <MapPinIcon className="h-4 w-4 mr-1" data-id="element-13111" data-mp="bnnrus4o4wlp" />
                    {profile.location}
                  </span>}
                <span className="flex items-center" data-id="element-13112" data-mp="gm7c8o3m60by">
                  <CalendarIcon className="h-4 w-4 mr-1" data-id="element-13113" data-mp="8khkde1eg9fn" />
                  Joined {profile.joinDate}
                </span>
              </div>

              {/* Social Links */}
              <div className="flex gap-3 mt-3" data-id="element-13114" data-mp="ij5qpfk0v03i">
                {profile.socialLinks.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className={`${dk.textMuted} hover:${rt.textPrimary} transition-colors`} data-id="element-13115" data-mp="djt48vx2bv1g" data-mpi={i}>
                    {link.platform === 'Instagram' && <InstagramIcon className="h-5 w-5" data-id="element-13116" data-mp="118lv4dytvcp7" />}
                    {link.platform === 'Twitter' && <TwitterIcon className="h-5 w-5" data-id="element-13117" data-mp="wd6ifpck3mud" />}
                    {link.platform === 'Goodreads' && <BookIcon className="h-5 w-5" data-id="element-13118" data-mp="169t0659l5xs6" />}
                    {!['Instagram', 'Twitter', 'Goodreads'].includes(link.platform) && <GlobeIcon className="h-5 w-5" data-id="element-13119" data-mp="1twwwo3q5zehw" />}
                  </a>)}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Tabs & Content */}
        <div className={`rounded-lg shadow-md overflow-hidden ${dk.card}`} data-id="element-13120" data-mp="1kvgp03qqhqhd">
          {/* Tabs */}
          <div className={`border-b ${dk.border}`} data-id="element-13121" data-mp="166aomcg5tu7w">
            <div className="flex overflow-x-auto" data-id="element-13122" data-mp="165j5w27nvqz1">
              {[{
              id: 'about',
              label: 'About',
              icon: <UserIcon className="h-4 w-4" data-id="element-13123" data-mp="x6xf5js1d5n6" />
            }, {
              id: 'preferences',
              label: 'Reading Preferences',
              icon: <BookIcon className="h-4 w-4" data-id="element-13124" data-mp="n887skxrn3l6" />
            }, {
              id: 'bookshelf',
              label: 'Bookshelf',
              icon: <BookOpenIcon className="h-4 w-4" data-id="element-13125" data-mp="s95kii00b5fa" />
            }, {
              id: 'community',
              label: 'Community',
              icon: <HeartIcon className="h-4 w-4" data-id="element-13126" data-mp="13oselylyqdn2" />
            }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === tab.id ? rt.activeTab : `${dk.textMuted} hover:${dk.textSecondary}`}`} data-id="element-13127" data-mp="yn35ioiawa1m">
                  <span className="mr-2" data-id="element-13128" data-mp="jxdpxo9go3ht">{tab.icon}</span>
                  {tab.label}
                </button>)}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6" data-id="element-13129" data-mp="awp80igy5izn">
            {/* About Tab */}
            {activeTab === 'about' && <div className="space-y-6" data-id="element-13130" data-mp="61tch8ci4dhs">
                <div data-id="element-13131" data-mp="10cf3mt8795y">
                  <h2 className={`text-lg font-medium mb-3 ${dk.textHeading}`} data-id="element-13132" data-mp="11x8h93t0m28">
                    About
                  </h2>
                  <p className={`${dk.textSecondary} whitespace-pre-line`} data-id="element-13133" data-mp="3n45yzm5jx1k3">
                    {profile.bio}
                  </p>
                </div>

                {/* Vibe Images */}
                {profile.vibeImages.length > 0 && <div data-id="element-13134" data-mp="5u7xv5mojavy">
                    <h2 className={`text-lg font-medium mb-3 ${dk.textHeading}`} data-id="element-13135" data-mp="5sn59ljxe20q">
                      Reader Vibe
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-13136" data-mp="39bq4j9qpubus">
                      {profile.vibeImages.map((img, i) => <div key={i} className={`aspect-square rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow ${dk.surfaceMuted}`} data-id="element-13137" data-mp="3e7onght9z1nt" data-mpi={i}>
                          <img src={img.url} alt={`Reader vibe ${i + 1}`} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" loading="lazy" data-id="element-13138" data-mp="2qjxcbhjh0uo4" />
                        </div>)}
                    </div>
                  </div>}

                {/* Currently Reading */}
                <div data-id="element-13139" data-mp="1chgmgl7k9tla">
                  <h2 className={`text-lg font-medium mb-3 ${dk.textHeading}`} data-id="element-13140" data-mp="fu28qvtakaqy">
                    Currently Reading
                  </h2>
                  <div className={`flex items-center p-4 rounded-lg ${dk.isDark ? 'bg-indigo-900/20 border border-indigo-800/30' : 'bg-indigo-50 border border-indigo-100'}`} data-id="element-13141" data-mp="kt523izwssj3">
                    <div className={`h-16 w-12 rounded flex items-center justify-center mr-4 ${dk.isDark ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-100 text-indigo-500'}`} data-id="element-13142" data-mp="tm8fjrak15dq">
                      <BookOpenIcon className="h-8 w-8" data-id="element-13143" data-mp="3pqiwqwv9li3g" />
                    </div>
                    <div data-id="element-13144" data-mp="3poetmh56wn48">
                      <div className={`font-medium ${dk.textPrimary}`} data-id="element-13145" data-mp="3kro658r22p8s">
                        {profile.currentRead}
                      </div>
                      <div className={`text-sm ${dk.textMuted}`} data-id="element-13146" data-mp="3go9neabqfurn">
                        Currently reading
                      </div>
                    </div>
                  </div>
                </div>

                {/* Giving Back Section */}
                {readerDonationData.isPublic && <div className={`border rounded-lg p-5 ${dk.border}`} data-id="element-13147" data-mp="31zdksssg39yf">
                    <div className="flex items-center mb-4" data-id="element-13148" data-mp="2x2vhlxcrjjo7">
                      <div className={`p-2 rounded-full mr-3 ${dk.isDark ? 'bg-rose-900/30' : 'bg-rose-100'}`} data-id="element-13149" data-mp="2t08m9mgvvgm9">
                        <GiftIcon className={`h-5 w-5 ${dk.isDark ? 'text-rose-400' : 'text-rose-500'}`} data-id="element-13150" data-mp="2o0m9fe4yw2dz" />
                      </div>
                      <div data-id="element-13151" data-mp="29df8m1974kst">
                        <h2 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-13152" data-mp="29d5ga70oi1jy">
                          Giving Back
                        </h2>
                        <p className={`text-sm ${dk.textSecondary}`} data-id="element-13153" data-mp="1zgsojeop1xru">
                          Supporting causes through purchases
                        </p>
                      </div>
                    </div>

                    {/* Total Donated */}
                    <div className={`rounded-lg p-4 mb-4 ${dk.isDark ? 'bg-rose-900/20 border border-rose-800/30' : 'bg-rose-50 border border-rose-100'}`} data-id="element-13154" data-mp="2s715j37huc4u">
                      <p className={`text-sm font-medium mb-1 ${dk.isDark ? 'text-rose-300' : 'text-rose-700'}`} data-id="element-13155" data-mp="2id26we6mpahn">
                        Total Contributed
                      </p>
                      <p className={`text-2xl font-bold ${dk.isDark ? 'text-rose-200' : 'text-rose-800'}`} data-id="element-13156" data-mp="3fu91guj188u7">
                        $
                        {readerDonationData.totalDonated.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                      </p>
                    </div>

                    {/* Supported Causes */}
                    <div data-id="element-13157" data-mp="lhqt2kgc4bdb">
                      <p className={`text-sm font-medium mb-3 ${dk.textSecondary}`} data-id="element-13158" data-mp="lh7jbr0u8tad">
                        Supported Causes
                      </p>
                      <div className="space-y-2" data-id="element-13159" data-mp="uix9yumwbo73">
                        {readerDonationData.causes.map(cause => <Link key={cause.id} to={`/donations/nonprofit/${cause.slug}`} className={`flex items-center p-3 rounded-lg transition-colors ${dk.surfaceMuted} hover:${dk.cardElevated} ${dk.textPrimary}`} data-id="element-13160" data-mp="qi53281hxvdz">
                            <HeartIcon className={`h-4 w-4 mr-2 flex-shrink-0 ${dk.isDark ? 'text-rose-400' : 'text-rose-500'}`} data-id="element-13161" data-mp="316621bw7lbkp" />
                            <span className="text-sm font-medium" data-id="element-13162" data-mp="3b4lubi6z2qvs">
                              {cause.name}
                            </span>
                          </Link>)}
                      </div>
                    </div>
                  </div>}
              </div>}

            {/* Reading Preferences Tab */}
            {activeTab === 'preferences' && <div className="space-y-6" data-id="element-13163" data-mp="3tvmrc0oj8ccp">
                {/* Genres */}
                <div data-id="element-13164" data-mp="2p27b0wwa4p6">
                  <h2 className={`text-lg font-medium mb-3 ${dk.textHeading}`} data-id="element-13165" data-mp="2pv356ubf9sj">
                    Favorite Genres
                  </h2>
                  <div className="flex flex-wrap gap-2" data-id="element-13166" data-mp="7oeqnbok4tn0">
                    {profile.genres.map((genre, i) => <span key={i} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`} data-id="element-13167" data-mp="1s44gj3ntlc2k" data-mpi={i}>
                        <BookIcon className="h-3.5 w-3.5 mr-1" data-id="element-13168" data-mp="1da8ajbf50vii" />
                        {genre}
                      </span>)}
                  </div>
                </div>

                {/* Tropes */}
                <div data-id="element-13169" data-mp="25yduhvydagha">
                  <h2 className={`text-lg font-medium mb-3 ${dk.textHeading}`} data-id="element-13170" data-mp="25zyhn0icqx54">
                    Favorite Tropes
                  </h2>
                  <div className="flex flex-wrap gap-2" data-id="element-13171" data-mp="1w8khqrmhnvbg">
                    {profile.tropes.map((trope, i) => <span key={i} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${dk.isDark ? 'bg-pink-900/30 text-pink-300' : 'bg-pink-100 text-pink-800'}`} data-id="element-13172" data-mp="2tnyfslhztptq" data-mpi={i}>
                        <HeartIcon className="h-3.5 w-3.5 mr-1" data-id="element-13173" data-mp="rxr9xrmgq3ca" />
                        {trope}
                      </span>)}
                  </div>
                </div>

                {/* Reading Style */}
                <div data-id="element-13174" data-mp="i762rfsq3qhr">
                  <h2 className={`text-lg font-medium mb-3 ${dk.textHeading}`} data-id="element-13175" data-mp="i6mrghxkw0cp">
                    Reading Style
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-13176" data-mp="8a1pk5guv5d3">
                    <div className={`p-4 rounded-lg ${dk.surfaceMuted}`} data-id="element-13177" data-mp="87x9yq9lghwz">
                      <div className="flex items-center mb-2" data-id="element-13178" data-mp="3qgy39b0zwbo5">
                        <FlameIcon className="h-5 w-5 text-orange-500 mr-2" data-id="element-13179" data-mp="3ve7qql4tqd9c" />
                        <span className={`font-medium ${dk.textPrimary}`} data-id="element-13180" data-mp="1oo9yuycm7i6s">
                          Burn Level
                        </span>
                      </div>
                      <div className={dk.textSecondary} data-id="element-13181" data-mp="1tml93a6tnupj">
                        {profile.burnLevel}
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${dk.surfaceMuted}`} data-id="element-13182" data-mp="396u6ga3ib7c2">
                      <div className="flex items-center mb-2" data-id="element-13183" data-mp="3nv8b8qkhh5v9">
                        <BookOpenIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-13184" data-mp="3nwrtgmpp9qzo" />
                        <span className={`font-medium ${dk.textPrimary}`} data-id="element-13185" data-mp="3j3gqiwo0dud1">
                          Preferred Format
                        </span>
                      </div>
                      <div className={dk.textSecondary} data-id="element-13186" data-mp="3rxbnviiw2gi4">{profile.format}</div>
                    </div>
                    <div className={`p-4 rounded-lg ${dk.surfaceMuted}`} data-id="element-13187" data-mp="aoyfqn6oo0pq">
                      <div className="flex items-center mb-2" data-id="element-13188" data-mp="5plrpz42snlk">
                        <TagIcon className={`h-5 w-5 mr-2 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-500'}`} data-id="element-13189" data-mp="5qddyjbs98z6" />
                        <span className={`font-medium ${dk.textPrimary}`} data-id="element-13190" data-mp="27hqjnalkrkwa">
                          Comfort Level
                        </span>
                      </div>
                      <div className={dk.textSecondary} data-id="element-13191" data-mp="1qmmcv5hon00x">
                        {profile.comfortLevel}
                      </div>
                    </div>
                    {profile.triggers && <div className={`p-4 rounded-lg ${dk.surfaceMuted}`} data-id="element-13192" data-mp="20cfm3zucr320">
                        <div className="flex items-center mb-2" data-id="element-13193" data-mp="20eic4jvi8w6q">
                          <SettingsIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-13194" data-mp="12q8gcmmbb4o2" />
                          <span className={`font-medium ${dk.textPrimary}`} data-id="element-13195" data-mp="17p30brw0mz9r">
                            Content Triggers
                          </span>
                        </div>
                        <div className={dk.textSecondary} data-id="element-13196" data-mp="1gp7x783l2f9o">
                          {profile.triggers}
                        </div>
                      </div>}
                  </div>
                </div>
              </div>}

            {/* Bookshelf Tab */}
            {activeTab === 'bookshelf' && <div className="space-y-6" data-id="element-13197" data-mp="gg05s3favjpg">
                {/* View Toggle */}
                <div className={`flex items-center justify-center rounded-lg p-1 mb-6 ${dk.isDark ? 'bg-gray-800' : 'bg-gray-100'}`} data-id="element-13198" data-mp="cfzgtoukrq45">
                  <button className={`flex items-center px-4 py-2 rounded-md transition-colors ${bookshelfView === 'recentlyRead' ? dk.isDark ? 'bg-gray-700 text-white shadow-sm' : 'bg-white shadow-sm text-indigo-600' : `${dk.textMuted} hover:${dk.textPrimary}`}`} onClick={() => setBookshelfView('recentlyRead')} data-id="element-13199" data-mp="2pd3606f59o3">
                    <BookOpenIcon className="h-4 w-4 mr-2" data-id="element-13200" data-mp="3kwkqz8yy0p8u" />
                    <span className="font-medium" data-id="element-13201" data-mp="3fwycyxbaj4yz">Recently Read</span>
                  </button>
                  <button className={`flex items-center px-4 py-2 rounded-md transition-colors ${bookshelfView === 'toBeRead' ? dk.isDark ? 'bg-gray-700 text-white shadow-sm' : 'bg-white shadow-sm text-indigo-600' : `${dk.textMuted} hover:${dk.textPrimary}`}`} onClick={() => setBookshelfView('toBeRead')} data-id="element-13202" data-mp="1j220henk1xlt">
                    <BookIcon className="h-4 w-4 mr-2" data-id="element-13203" data-mp="1xoi9jb5t1pfw" />
                    <span className="font-medium" data-id="element-13204" data-mp="1xmyt9kk5fee4">To Be Read</span>
                  </button>
                </div>

                {/* Recently Read Section */}
                {bookshelfView === 'recentlyRead' && <div className={`border rounded-lg overflow-hidden ${dk.border}`} data-id="element-13205" data-mp="1jpa8ecq30792">
                    <button onClick={() => setShowSectionContent(!showSectionContent)} className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${dk.surfaceMuted} hover:${dk.cardElevated}`} data-id="element-13206" data-mp="1epnvy5i2uv1b">
                      <div className="flex items-center" data-id="element-13207" data-mp="34540t8oyxule">
                        <BookOpenIcon className={`h-5 w-5 mr-2 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-13208" data-mp="345ndoazrhssk" />
                        <h2 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-13209" data-mp="3gmy64p5p1qt3">
                          Recently Read
                        </h2>
                        <span className={`ml-2 text-sm ${dk.textMuted}`} data-id="element-13210" data-mp="3lgsj79qsn7j7">
                          ({profile.recentlyRead.length})
                        </span>
                      </div>
                      {showSectionContent ? <ChevronUpIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-13211" data-mp="2xoc3h5uj3rn2" /> : <ChevronDownIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-13212" data-mp="32now6782zsxk" />}
                    </button>
                    {showSectionContent && <div className="p-4" data-id="element-13213" data-mp="hx7millludfn">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-13214" data-mp="mwk9r7ge1mir">
                          {profile.recentlyRead.map((book, i) => <div key={i} className="flex flex-col items-center" data-id="element-13215" data-mp="2j1s24266vgca" data-mpi={i}>
                              <Link to={`/book/${encodeURIComponent(book.title)}`} className="relative block bg-indigo-100 aspect-[2/3] w-full rounded shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-id="element-13216" data-mp="2e0m8810sln16">
                                {/* Series Badge (Left) */}
                                {book.series && <div className="absolute top-2 left-2 bg-indigo-600 text-white text-xs font-bold px-2 py-1 rounded z-10" data-id="element-13217" data-mp="1v8jo2foxzom6">
                                    {book.series}
                                  </div>}
                                {/* Rating Badge (Right) */}
                                <div className="absolute top-2 right-2 bg-yellow-400 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center z-10" data-id="element-13218" data-mp="1ckeolkfbipt6">
                                  {book.rating}
                                </div>
                                {/* Book Cover Image */}
                                <img src={'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'} alt={`Cover for ${book.title}`} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" loading="lazy" data-id="element-13219" data-mp="sitkmlofn35x" />
                              </Link>
                            </div>)}
                        </div>
                      </div>}
                  </div>}

                {/* To Be Read Section */}
                {bookshelfView === 'toBeRead' && <div className={`border rounded-lg overflow-hidden ${dk.border}`} data-id="element-13220" data-mp="l1f4o31z9l2w">
                    <button onClick={() => setShowSectionContent(!showSectionContent)} className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${dk.surfaceMuted} hover:${dk.cardElevated}`} data-id="element-13221" data-mp="b9ix81e04im0">
                      <div className="flex items-center" data-id="element-13222" data-mp="13vu6e0fobruf">
                        <BookIcon className={`h-5 w-5 mr-2 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-13223" data-mp="z0eu3cxbe404" />
                        <h2 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-13224" data-mp="p160wma9wtet">
                          To Be Read
                        </h2>
                        <span className={`ml-2 text-sm ${dk.textMuted}`} data-id="element-13225" data-mp="3ersxjas2r78h">
                          ({profile.tbr.length})
                        </span>
                      </div>
                      {showSectionContent ? <ChevronUpIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-13226" data-mp="1p79k8ihz0933" /> : <ChevronDownIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-13227" data-mp="1z7a88hqsbddm" />}
                    </button>
                    {showSectionContent && <div className="p-4" data-id="element-13228" data-mp="28xv0lnfv7lo3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-13229" data-mp="28z65jql1qzgs">
                          {profile.tbr.map((book, i) => <div key={i} className="flex flex-col items-center" data-id="element-13230" data-mp="2hz30ub1xywkt" data-mpi={i}>
                              <Link to={`/book/${encodeURIComponent(book)}`} className="relative block bg-indigo-100 aspect-[2/3] w-full rounded shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-id="element-13231" data-mp="2mx4kax3a4xwt">
                                {/* Book Cover Image */}
                                <img src={`https://images.unsplash.com/photo-1589998059171-988d887df646?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80&random=${i}`} alt={`Cover for ${book}`} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" loading="lazy" data-id="element-13232" data-mp="kjyunoxzovx4" />
                              </Link>
                            </div>)}
                        </div>
                      </div>}
                  </div>}
              </div>}

            {/* Community Tab */}
            {activeTab === 'community' && <div className="space-y-6" data-id="element-13233" data-mp="1n2wc7dasx3qo">
                {/* Following Authors */}
                <div data-id="element-13234" data-mp="1i7qtsogly77c">
                  <h2 className={`text-lg font-medium mb-3 ${dk.textHeading}`} data-id="element-13235" data-mp="1i433dy6gm0wu">
                    Following Authors
                  </h2>
                  <div className="flex flex-wrap gap-2" data-id="element-13236" data-mp="25s9n9ul5j1xq">
                    {profile.following.authors.map((author, i) => <span key={i} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${dk.isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'}`} data-id="element-13237" data-mp="1rt9z0o1jwp23" data-mpi={i}>
                        <UserIcon className="h-3.5 w-3.5 mr-1" data-id="element-13238" data-mp="1muyrijyqaqn1" />
                        {author}
                      </span>)}
                  </div>
                </div>

                {/* Following Vendors */}
                <div data-id="element-13239" data-mp="1e2mz4sk20t4f">
                  <h2 className={`text-lg font-medium mb-3 ${dk.textHeading}`} data-id="element-13240" data-mp="1e00vh40aaxrh">
                    Following Vendors
                  </h2>
                  <div className="flex flex-wrap gap-2" data-id="element-13241" data-mp="144h2apd3ud6f">
                    {profile.following.vendors.map((vendor, i) => <span key={i} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${dk.isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-13242" data-mp="1jknrl402qexp" data-mpi={i}>
                        <ShopIcon className="h-3.5 w-3.5 mr-1" data-id="element-13243" data-mp="15mfu8uz4lbm0" />
                        {vendor}
                      </span>)}
                  </div>
                </div>

                {/* Following Readers */}
                <div data-id="element-13244" data-mp="vvkxuxs60vmu">
                  <h2 className={`text-lg font-medium mb-3 ${dk.textHeading}`} data-id="element-13245" data-mp="vv354b4pozjo">
                    Following Readers
                  </h2>
                  <div className="flex flex-wrap gap-2" data-id="element-13246" data-mp="mvy3gvxusq3a">
                    {profile.following.readers.map((reader, i) => <span key={i} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`} data-id="element-13247" data-mp="hvk03r7764bl" data-mpi={i}>
                        <UserIcon className="h-3.5 w-3.5 mr-1" data-id="element-13248" data-mp="7sekworv9euo" />@{reader}
                      </span>)}
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}